import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { getTranslations, type UITranslations } from "../translations/ui";

export type Locale = "en" | "ru" | "cs";
export const SUPPORTED_LOCALES: Locale[] = ["en", "ru", "cs"];
export const DEFAULT_LOCALE: Locale = "cs";

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: UITranslations;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Определение языка браузера
export const getBrowserLocale = (): Locale => {
  if (typeof navigator === "undefined") {
    return DEFAULT_LOCALE;
  }

  const browserLang =
    navigator.language || (navigator as any).userLanguage || "en";
  const langCode = browserLang.split("-")[0].toLowerCase() as any;

  if (SUPPORTED_LOCALES.includes(langCode)) {
    return langCode;
  }

  return DEFAULT_LOCALE;
};

// Ключ для localStorage
const LS_LOCALE_KEY = "app-locale";

interface LocaleProviderProps {
  children: ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  const location = useLocation();

  // Определяем локаль на основе текущего пути
  const getLocaleFromPath = (path: string): Locale => {
    const segments = path.split("/").filter(Boolean);
    const urlLocale = segments[0] as any;
    if (SUPPORTED_LOCALES.includes(urlLocale)) {
      return urlLocale;
    }
    return DEFAULT_LOCALE;
  };

  const [locale, setLocaleState] = useState<Locale>(() =>
    getLocaleFromPath(window.location.pathname)
  );

  // Синхронизация локали с URL при каждом изменении пути
  useEffect(() => {
    const newLocale = getLocaleFromPath(location.pathname);
    if (newLocale !== locale) {
      setLocaleState(newLocale);
    }
  }, [location.pathname, locale]);

  // Сохраняем локаль в localStorage и обновляем атрибут lang у html
  useEffect(() => {
    localStorage.setItem(LS_LOCALE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  const t = getTranslations(locale);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

// Hook для использования переводов
export function useTranslation() {
  const { t } = useLocale();
  return t;
}
